import { useState } from 'react';
import { t, plural } from '@lingui/core/macro';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';
import { useBookmarkPageContent } from '../../contexts/BookmarkPageContext';
import { useBatchActions } from '../../hooks/useBatchActions';
import BatchTagPopover from './BatchTagPopover';
import BatchMovePopover from './BatchMovePopover';
import ButtonIcon from '../ui/ButtonIcon';
import Popover from '../ui/Popover';
import type { ViewMode } from '../../types/api';

interface BookmarkBatchOperationsProps {
  viewMode?: ViewMode;
}

function BookmarkBatchOperations({ viewMode = 'unread' }: BookmarkBatchOperationsProps) {
  const { selectedBookmarkIds, clearSelection, selectAll } = useBookmarkSelection();
  const { batchArchive, batchDelete, batchUnarchive } = useBatchActions();
  const bookmarkPage = useBookmarkPageContent();
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [showMovePopover, setShowMovePopover] = useState(false);

  const selectedCount = selectedBookmarkIds.size;
  const allBookmarkIds = bookmarkPage?.bookmarks.map((bookmark) => bookmark.id) || [];
  const allSelected =
    allBookmarkIds.length > 0 &&
    allBookmarkIds.every((id) => selectedBookmarkIds.has(String(id)));
  const someSelected = selectedCount > 0 && !allSelected;

  const isArchiveView = viewMode === 'archive';

  const handleBatchArchive = async () => {
    if (selectedCount === 0) return;

    if (isArchiveView) {
      await batchUnarchive.mutateAsync(Array.from(selectedBookmarkIds));
    } else {
      await batchArchive.mutateAsync(Array.from(selectedBookmarkIds));
    }
    clearSelection();
  };

  const handleBatchDelete = async () => {
    if (selectedCount === 0) return;

    if (
      window.confirm(
        plural(selectedCount, {
          one: 'Are you sure you want to delete # article?',
          other: 'Are you sure you want to delete # articles?',
        }),
      )
    ) {
      await batchDelete.mutateAsync(Array.from(selectedBookmarkIds));
      clearSelection();
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll(allBookmarkIds);
    }
  };

  return (
    <div className="flex w-full items-center space-x-4">
      <div className="batch-operations-slide-in flex items-center space-x-1">
        {allBookmarkIds.length > 0 && (
          <button
            onClick={handleSelectAll}
            className="flex items-center pr-3"
            title={allSelected ? t`Deselect all` : t`Select all`}
          >
            <div className="checkbox-button">
              {allSelected ? (
                // Checkmark icon when all selected
                <svg
                  width="7"
                  height="6"
                  viewBox="0 0 9 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 4.31034L3.69231 7L8 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : someSelected ? (
                // Dash/minus icon when some but not all selected
                <svg
                  width="6"
                  height="1"
                  viewBox="0 0 6 1"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line y1="0.5" x2="6" y2="0.5" stroke="currentColor" strokeWidth="1" />
                </svg>
              ) : null}
            </div>
          </button>
        )}

        <Popover
          open={showTagPopover}
          onOpenChange={setShowTagPopover}
          placement="bottom"
          preventCloseOnContentClick={true}
          content={
            <BatchTagPopover
              bookmarkIds={Array.from(selectedBookmarkIds)}
              onClose={() => setShowTagPopover(false)}
            />
          }
        >
          <ButtonIcon
            icon="tag"
            onClick={() => setShowTagPopover(true)}
            disabled={selectedCount === 0}
            title={t`Tag`}
            className="disabled:opacity-50"
          />
        </Popover>
        <Popover
          open={showMovePopover}
          onOpenChange={setShowMovePopover}
          placement="bottom"
          preventCloseOnContentClick={true}
          content={
            <BatchMovePopover
              bookmarkIds={Array.from(selectedBookmarkIds)}
              onClose={() => setShowMovePopover(false)}
            />
          }
        >
          <ButtonIcon
            icon="folder"
            onClick={() => setShowMovePopover(true)}
            disabled={selectedCount === 0}
            title={t`Move`}
            className="disabled:opacity-50"
          />
        </Popover>

        <ButtonIcon
          icon={isArchiveView ? 'archive-filled' : 'archive'}
          onClick={handleBatchArchive}
          disabled={
            selectedCount === 0 ||
            (isArchiveView ? batchUnarchive.isPending : batchArchive.isPending)
          }
          title={
            isArchiveView
              ? batchUnarchive.isPending
                ? t`Unarchiving...`
                : t`Unarchive`
              : batchArchive.isPending
                ? t`Archiving...`
                : t`Archive`
          }
          className="disabled:opacity-50"
        />

        <ButtonIcon
          icon="trash"
          onClick={handleBatchDelete}
          disabled={selectedCount === 0 || batchDelete.isPending}
          title={batchDelete.isPending ? t`Deleting...` : t`Delete`}
          className="text-danger hover:text-danger-hover disabled:opacity-50"
        />
      </div>
      {selectedCount > 0 && (
        <span className="text-copy-tertiary text-xs font-medium select-none">
          {selectedCount} / {allBookmarkIds.length}
        </span>
      )}
    </div>
  );
}

export default BookmarkBatchOperations;
